// ============================================================================
// CLASS NODE - Entity class with instances, progress bar, and inline add
// ============================================================================

import { useState, useRef, useEffect } from 'react'
import { ChevronRight, Users, Target, Layers, Lightbulb, Plus, Sparkles } from 'lucide-react'
import { InstanceItem } from './InstanceItem'
import type { EntityTreeClassGroup, EntityTreeInstance } from '@/lib/api/client'
import type { PhaseEntityRequirement } from '@/hooks/useEntityTree'
import type { EntityDomain } from '@ispot/shared'

interface ClassNodeProps {
  classGroup: EntityTreeClassGroup
  requirement?: PhaseEntityRequirement
  showProgress?: boolean
  showValidationActions?: boolean
  /** Enable inline entity creation */
  showInlineAdd?: boolean
  /** Enable AI suggestions button */
  showAISuggestions?: boolean
  onConfirm: (entityUri: string) => void
  onReject: (entityUri: string) => void
  onEntitySelect?: (entity: EntityTreeInstance) => void
  /** Callback when AI suggestions button is clicked */
  onAISuggestionsClick?: (classGroup: EntityTreeClassGroup) => void
  selectedClassUri?: string
  selectedEntityUri?: string
  /** Callback when a new entity is created inline */
  onCreateEntity?: (classUri: string, label: string) => Promise<void>
}

// Domain-specific icon backgrounds
const DOMAIN_ICON_CLASSES: Record<EntityDomain, string> = {
  market: 'bg-market/10 text-market',
  business: 'bg-business/10 text-business',
  solution: 'bg-solution/10 text-solution',
}

// Map common class names to icons
function getClassIcon(className: string) {
  const lowerName = className.toLowerCase()
  if (lowerName.includes('persona') || lowerName.includes('customer') || lowerName.includes('segment')) {
    return Users
  }
  if (lowerName.includes('job') || lowerName.includes('goal') || lowerName.includes('objective')) {
    return Target
  }
  if (lowerName.includes('feature') || lowerName.includes('requirement')) {
    return Lightbulb
  }
  return Layers
}

export function ClassNode({
  classGroup,
  requirement,
  showProgress = true,
  showValidationActions = true,
  showInlineAdd = true,
  showAISuggestions = false,
  onConfirm,
  onReject,
  onEntitySelect,
  onAISuggestionsClick,
  selectedClassUri,
  selectedEntityUri,
  onCreateEntity,
}: ClassNodeProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isAdding, setIsAdding] = useState(false)
  const [newEntityLabel, setNewEntityLabel] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const { className, domain, instances } = classGroup
  const instanceCount = instances.length
  const confirmedCount = instances.filter((i) => i.status === 'confirmed').length
  const minCount = requirement?.minCount || 0

  // Progress for display
  const progress = minCount > 0
    ? Math.min(100, Math.round((confirmedCount / minCount) * 100))
    : 100

  const Icon = getClassIcon(className)

  // Focus input when adding
  useEffect(() => {
    if (isAdding && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isAdding])

  const handleAddClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsAdding(true)
    setIsExpanded(true)
  }

  // Handle class row click - only expand/collapse
  const handleClassClick = () => {
    setIsExpanded(!isExpanded)
  }

  // Handle AI suggestions button click
  const handleAISuggestionsClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onAISuggestionsClick?.(classGroup)
  }

  const isClassSelected = selectedClassUri === classGroup.classUri

  const handleCancelAdd = () => {
    setIsAdding(false)
    setNewEntityLabel('')
  }

  const handleSubmitAdd = async () => {
    if (!newEntityLabel.trim() || !onCreateEntity) return

    setIsSubmitting(true)
    try {
      await onCreateEntity(classGroup.classUri, newEntityLabel.trim())
      setNewEntityLabel('')
      setIsAdding(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSubmitAdd()
    } else if (e.key === 'Escape') {
      handleCancelAdd()
    }
  }

  return (
    <div className="mb-0.5">
      {/* Class header */}
      <div
        onClick={handleClassClick}
        className={`group w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md transition-colors cursor-pointer ${
          isClassSelected
            ? 'bg-warm-100 border border-warm-300'
            : 'hover:bg-warm-50 border border-transparent'
        }`}
      >
        <ChevronRight
          className={`w-3.5 h-3.5 text-warm-400 transition-transform ${
            isExpanded ? 'rotate-90' : ''
          }`}
        />

        {/* Icon */}
        <div className={`w-[18px] h-[18px] rounded flex items-center justify-center ${DOMAIN_ICON_CLASSES[domain]}`}>
          <Icon className="w-3 h-3" />
        </div>

        {/* Class name */}
        <span className="flex-1 text-left text-xs font-medium text-warm-700">
          {className}
        </span>

        {/* Count indicator */}
        {showProgress && minCount > 0 ? (
          <span className="text-[10px] text-warm-400 mr-1">
            {confirmedCount} / {minCount}
          </span>
        ) : (
          <span className="text-[10px] text-warm-400 mr-1">
            {instanceCount}
          </span>
        )}

        {/* Progress bar */}
        {showProgress && minCount > 0 && (
          <div className="w-10 h-1 bg-warm-200 rounded overflow-hidden">
            <div
              className={`h-full rounded transition-all ${
                progress >= 100 ? 'bg-market' : 'bg-warm-400'
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        {/* AI suggestions button */}
        {showAISuggestions && onAISuggestionsClick && (
          <button
            onClick={handleAISuggestionsClick}
            className={`w-5 h-5 flex items-center justify-center border rounded transition-all ${
              isClassSelected
                ? 'border-warm-400 bg-warm-100 text-warm-600'
                : 'border-warm-200 bg-white text-warm-300 hover:border-warm-400 hover:text-warm-500 hover:bg-warm-50'
            }`}
            title={`AI suggestions for ${className}`}
          >
            <Sparkles className="w-3 h-3" />
          </button>
        )}

        {/* Add button - always visible but subtle, more prominent on hover */}
        {showInlineAdd && onCreateEntity && (
          <button
            onClick={handleAddClick}
            className="w-5 h-5 flex items-center justify-center border border-warm-200 bg-white rounded text-warm-300 hover:border-market hover:text-market hover:bg-market/5 transition-all"
            title={`Add ${className}`}
          >
            <Plus className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Instances */}
      {isExpanded && (
        <div className="ml-6">
          {instances.map((instance) => (
            <InstanceItem
              key={instance.uri}
              instance={instance}
              showValidationActions={showValidationActions}
              onConfirm={onConfirm}
              onReject={onReject}
              onSelect={onEntitySelect}
              isSelected={selectedEntityUri === instance.uri}
            />
          ))}

          {/* Inline add form */}
          {isAdding && (
            <div className="flex items-center gap-2 px-2.5 py-1.5">
              <input
                ref={inputRef}
                type="text"
                value={newEntityLabel}
                onChange={(e) => setNewEntityLabel(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={`New ${className.toLowerCase()}...`}
                className="flex-1 px-2 py-1 text-xs border border-warm-300 rounded focus:border-market focus:ring-1 focus:ring-market/20 outline-none"
                disabled={isSubmitting}
              />
              <button
                onClick={handleSubmitAdd}
                disabled={!newEntityLabel.trim() || isSubmitting}
                className="px-2 py-1 text-[10px] font-medium bg-market text-white rounded hover:bg-market/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? '...' : 'Add'}
              </button>
              <button
                onClick={handleCancelAdd}
                disabled={isSubmitting}
                className="px-2 py-1 text-[10px] font-medium border border-warm-300 text-warm-600 rounded hover:border-warm-400 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          )}

          {/* Empty state */}
          {instances.length === 0 && !isAdding && (
            <div className="py-2 text-xs text-warm-400 italic">
              No instances yet
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ClassNode
